import { Dispatch, SetStateAction, useState } from 'react';
import { MatchConfirmedContent } from './MatchConfirmedContent';
import { MatchConfirmingContent } from './MatchConfirmingContent';
import { MatchDeclinedContent } from './MatchDeclinedContent';
import { MatchDecliningContent } from './MatchDecliningContent';
import { MatchNeedHelpContent } from './MatchNeedHelpContent';
import { Modal, ModalCallToAction } from '@platform/components';
import { NameCustomProjectContent } from './NameCustomProjectContent';
import * as Sentry from '@sentry/react';
import { OtherUpdateContent } from './OtherUpdateContent';
import { OutcomeSelectionContent } from './OutcomeSelectionContent';
import { UnresponsiveContent } from './UnresponsiveContent';
import { UpdateConfirmedContent } from './UpdateConfirmedContent';
import { UpdateSelectionContent } from './UpdateSelectionContent';
import { useQueryClient } from '@tanstack/react-query';
import { AssignmentDeclinedReason, SimplifiedUserRole } from '@platform/client/schemas';
import { Discovery, MyProjects, ProjectHub } from '@platform/client';
import { navigate } from 'wouter/use-browser-location';
import { ROUTES } from 'apps/web/src/app/routes/consts';
import { useForm, UseFormTrigger } from 'react-hook-form';

export enum DiscoveryCallModalStage {
  //Initial stage
  Update_Selection = 'update-selection',

  Outcome_Selection = 'outcome-selection',
  Match_Confirming = 'match-confirming',
  Custom_Naming = 'custom-naming',
  Match_Confirmed = 'match-confirmed',
  Match_Declining = 'match-declining',
  Match_Declined = 'match-declined',
  Match_Need_Help = 'match-need-help',

  Unresponsive = 'unresponsive',
  Other = 'other',
  Update_Confirmed = 'other-confirmed',
}

export enum CompletedOutcomes {
  Accepted = 'accepted',
  Declined = 'declined',
  Need_Help = 'need-help',
}

export enum UpdateType {
  Completed = 'completed',
  // TODO: Where is this used?
  Scheduled = 'scheduled',
  Unresponsive = 'unresponsive',
  Other = 'other',
}

export type DiscoveryModalDataProps = {
  introductionId: number;
  assignmentDiscoveryId: number;
  otherPartyName: string;
  userType: SimplifiedUserRole;
  projectId?: number | null;
  projectName?: string | null;
};

export type DiscoveryModalProps = DiscoveryModalDataProps & {
  onClose: () => void;
};

export type UpdateDiscoveryCallFormData = {
  introductionId: number;
  updateType: UpdateType | null;
  outcome: CompletedOutcomes | null;
  reason: AssignmentDeclinedReason | null;
  unresponsiveDetails: string | null;
  otherUpdateDetails: string | null;
  needHelpDetails: string | null;
  declineReasonDetails: string | null;
  projectName: string | null;
};

export const DiscoveryCallUpdateModal = (props: DiscoveryModalProps) => {
  const { assignmentDiscoveryId, otherPartyName, onClose, userType } = props;

  // without access to CTA in individual components to allow for clean RHF handleSubmit validation and state handling
  // but still wanting custom validation, we use trigger/watch/formState to manage required fields and values.
  // register / setValue are used in existing RadioGroup components, while control is used in existing FormsMultilineInput component.
  // Might be worth considering a re-factor to allow for more complicated modal workflows in the future and a more cohesive RHF strategy
  const { register, setValue, control, trigger, formState, watch } =
    useForm<UpdateDiscoveryCallFormData>({ mode: 'onChange' });
  const selectedUpdateType = watch('updateType');
  const selectedOutcome = watch('outcome');
  const needHelpDetails = watch('needHelpDetails');
  const unresponsiveDetails = watch('unresponsiveDetails');
  const otherUpdateDetails = watch('otherUpdateDetails');
  const selectedReason = watch('reason');
  const declineReasonDetails = watch('declineReasonDetails');
  const customProjectName = watch('projectName');

  // Tracks whether the workflow mutation is currently ongoing and whether we should show
  // a loading state
  const [isSubmissionSaving, setIsSubmissionSaving] = useState(false);
  const [modalStage, setModalStage] = useState<DiscoveryCallModalStage>(
    DiscoveryCallModalStage.Update_Selection,
  );
  // If set, submitting the discovery caused an error and we display a generic error message
  const [isSubmissionErrored, setIsSubmissionErrored] = useState(false);
  const queryClient = useQueryClient();
  const updateDiscoveryStatus = Discovery.useUpdateDiscoveryCall({
    mutation: {
      onMutate: () => {
        setIsSubmissionSaving(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getCountUserActionItemsQueryKey() });
        setIsSubmissionSaving(false);
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataQueryKey({
            assignment_discovery_id: assignmentDiscoveryId,
          }),
        });
      },
      onError: (error) => {
        Sentry.captureException(error);
        setIsSubmissionSaving(false);
      },
    },
  });

  const [startDate, setStartDate] = useState<Date>(new Date());
  const [customName, setCustomName] = useState<string>('');
  let initialEndDate = new Date();
  //Default end date is 3 months in the future
  initialEndDate = new Date(initialEndDate.setDate(initialEndDate.getDate() + 90));
  const [endDate, setEndDate] = useState<Date>(initialEndDate);

  const closeModal = () => {
    setModalStage(DiscoveryCallModalStage.Update_Selection);
    setIsSubmissionErrored(false);
    setStartDate(new Date());
    setEndDate(initialEndDate);
    setCustomName('');
    onClose();
  };

  const secondaryCTA = getSecondaryCTA(modalStage, setModalStage, closeModal);

  return (
    <Modal
      isOpen={true}
      headerDivision='step'
      progress={getProgress(modalStage)}
      onClose={closeModal}
      variant='full-screen'
      title={'Provide a Match Update'}
      primaryCTA={{
        ...getPrimaryCTA(
          userType,
          modalStage,
          setModalStage,
          selectedUpdateType,
          selectedOutcome,
          selectedReason,
          needHelpDetails,
          unresponsiveDetails,
          otherUpdateDetails,
          declineReasonDetails,
          customProjectName,
          closeModal,
          updateDiscoveryStatus,
          startDate,
          endDate,
          customName,
          props,
          setIsSubmissionErrored,
          trigger,
        ),
        isDisabled: () => isSubmissionSaving,
      }}
      secondaryCTA={
        secondaryCTA ? { ...secondaryCTA, isDisabled: () => isSubmissionSaving } : undefined
      }
    >
      {modalStage === DiscoveryCallModalStage.Update_Selection && (
        <UpdateSelectionContent
          register={register}
          setValue={setValue}
          selectedUpdateType={selectedUpdateType}
          formState={formState}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Unresponsive && (
        <UnresponsiveContent
          control={control}
          otherPartyName={otherPartyName}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Other && (
        <OtherUpdateContent control={control} formState={formState} isSaving={isSubmissionSaving} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declining && (
        <MatchDecliningContent
          control={control}
          register={register}
          setValue={setValue}
          formState={formState}
          selectedReason={selectedReason ?? undefined}
          isSaving={isSubmissionSaving}
          userAccountType={userType}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declined && <MatchDeclinedContent />}
      {modalStage === DiscoveryCallModalStage.Match_Confirmed && (
        <MatchConfirmedContent otherPartyName={otherPartyName} userAccountType={userType} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Need_Help && (
        <MatchNeedHelpContent
          control={control}
          formState={formState}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Update_Confirmed && <UpdateConfirmedContent />}
      {modalStage === DiscoveryCallModalStage.Outcome_Selection && (
        <OutcomeSelectionContent
          register={register}
          setValue={setValue}
          formState={formState}
          selectedOutcome={selectedOutcome}
          otherPartyName={otherPartyName}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirming && (
        <MatchConfirmingContent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Custom_Naming && (
        <NameCustomProjectContent
          register={register}
          formState={formState}
          isSaving={isSubmissionSaving}
        />
      )}
      {isSubmissionErrored && (
        <div className='md:absolute md:left-0 md:right-0 text-center mt-12 text-error-500'>
          <p>Something went wrong while submitting your update. Please try again later.</p>
          <p>If the problem persists, please contact support.</p>
        </div>
      )}
    </Modal>
  );
};

const getPrimaryCTA = (
  userType: SimplifiedUserRole,
  modalStage: DiscoveryCallModalStage,
  setDiscoveryModalStage: (stage: DiscoveryCallModalStage) => void,
  selectedUpdateType: UpdateType | undefined | null,
  selectedOutcome: CompletedOutcomes | undefined | null,
  selectedReason: AssignmentDeclinedReason | undefined | null,
  needHelpDetails: string | undefined | null,
  unresponsiveDetails: string | undefined | null,
  otherUpdateDetails: string | undefined | null,
  declineReasonDetails: string | undefined | null,
  customProjectName: string | undefined | null,
  closeModal: () => void,
  updateDiscoveryStatus: ReturnType<typeof Discovery.useUpdateDiscoveryCall>,
  startDate: Date,
  endDate: Date,
  customName: string,
  modalProps: DiscoveryModalProps,
  setIsSubmissionErrored: Dispatch<SetStateAction<boolean>>,
  trigger: UseFormTrigger<UpdateDiscoveryCallFormData>,
): ModalCallToAction => {
  const introductionId: number = modalProps.introductionId;
  /** Returns options for mutate() so that we proceed to the correct modal state upon successful submission, and
   * display an error otherwise */
  const getMutateOptions = (
    successStage: DiscoveryCallModalStage,
  ): Parameters<(typeof updateDiscoveryStatus)['mutate']>[1] => {
    return {
      onSuccess: () => {
        setIsSubmissionErrored(false);
        setDiscoveryModalStage(successStage);
      },
      onError: () => {
        setIsSubmissionErrored(true);
      },
    };
  };

  if (modalStage === DiscoveryCallModalStage.Update_Selection) {
    return {
      text: 'Continue',
      callback: async () => {
        const isValid = await trigger('updateType');
        if (isValid) {
          if (selectedUpdateType == UpdateType.Completed) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Outcome_Selection);
          } else if (selectedUpdateType == UpdateType.Unresponsive) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Unresponsive);
          } else if (selectedUpdateType == UpdateType.Other) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Other);
          }
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Unresponsive) {
    return {
      text: 'Save & continue',
      callback: () => {
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId: introductionId,
              updateType: 'unresponsive',
              additionalDetails: unresponsiveDetails ?? '',
            },
          },
          getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Other) {
    return {
      text: 'Save & continue',
      callback: async () => {
        // other is required field
        const isValid = await trigger('otherUpdateDetails');
        if (isValid) {
          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId: introductionId,
                updateType: 'other',
                additionalDetails: otherUpdateDetails ?? '',
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
          );
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Declining) {
    return {
      text: 'Save & continue',
      callback: async () => {
        const isValid = await trigger('reason');
        const isDeclineDetailsValid = await trigger('declineReasonDetails');
        if (isValid && (selectedReason !== 'other' || isDeclineDetailsValid)) {
          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId: introductionId,
                updateType: 'discovery-completed',
                outcome: 'declined',
                reason: selectedReason as AssignmentDeclinedReason,
                // For some reason Orval's generated output expects this to be non-null even though
                // this specific variant allows it
                additionalDetails: declineReasonDetails ?? '',
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Match_Declined),
          );
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Declined) {
    return {
      text: userType === SimplifiedUserRole.PROFESSIONAL ? 'Close' : 'Find another Experteer',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Confirmed) {
    const exitToPage = userType === SimplifiedUserRole.PROFESSIONAL ? 'Project Hub' : 'My Projects';
    return {
      text: `View in ${exitToPage}`,
      callback: () => {
        if (userType === SimplifiedUserRole.PROFESSIONAL) {
          closeModal();
        } else {
          navigate(`platform${ROUTES.DASHBOARD}${ROUTES.MY_PROJECTS}`);
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Need_Help) {
    return {
      text: 'Save & continue',
      callback: async () => {
        const isValid = await trigger('needHelpDetails');
        if (isValid) {
          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId: introductionId,
                updateType: 'discovery-completed',
                additionalDetails: needHelpDetails as string,
                outcome: 'need-help',
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Update_Confirmed),
          );
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Update_Confirmed) {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Outcome_Selection) {
    return {
      text: 'Continue',
      callback: async () => {
        const isValid = await trigger('outcome');
        if (isValid) {
          if (selectedOutcome == CompletedOutcomes.Accepted) {
            // Professionals accepting a Discovery leads to a separate sequence of steps for
            // defining project dates & (if necessary) naming the custom projects.
            // However, Org Reps are only shown a confirmation screen instead as we consider the
            // pros' decision to be binding
            if (userType === SimplifiedUserRole.PROFESSIONAL) {
              setDiscoveryModalStage(DiscoveryCallModalStage.Match_Confirming);
            } else {
              updateDiscoveryStatus.mutate(
                {
                  data: {
                    introductionId: introductionId,
                    updateType: 'discovery-completed',
                    outcome: 'accepted-rep',
                  },
                },
                getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
              );
            }
          } else if (selectedOutcome == CompletedOutcomes.Declined) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Match_Declining);
          } else if (selectedOutcome == CompletedOutcomes.Need_Help) {
            setDiscoveryModalStage(DiscoveryCallModalStage.Match_Need_Help);
          }
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Match_Confirming) {
    return {
      text: 'Save & continue',
      callback: () => {
        if (!modalProps.projectName) {
          //If the project is custom, proceed to name it and create it serverside on completion
          setDiscoveryModalStage(DiscoveryCallModalStage.Custom_Naming);
        } else {
          // Otherwise, submit the acceptance in this step
          const startDateISO = startDate?.toISOString();
          const endDateISO = endDate?.toISOString();

          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId: introductionId,
                updateType: 'discovery-completed',
                // Note that only professionals can see this specific screen
                outcome: 'accepted-pro',
                // Send up only date component of the string as FastAPI `date` validation requires omitting
                // the time component
                startDate: startDateISO.substring(0, startDateISO.indexOf('T')),
                endDate: endDateISO.substring(0, endDateISO.indexOf('T')),
                projectName: modalProps.projectName,
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
          );
        }
      },
    };
  } else if (modalStage === DiscoveryCallModalStage.Custom_Naming) {
    return {
      text: 'Save & continue',
      callback: async () => {
        const startDateISO = startDate?.toISOString();
        const endDateISO = endDate?.toISOString();
        const isValid = await trigger('projectName');
        if (isValid) {
          updateDiscoveryStatus.mutate(
            {
              data: {
                introductionId,
                updateType: 'discovery-completed',
                // Note that only professionals can see this specific screen
                outcome: 'accepted-pro',
                // Send up only date component of the string as FastAPI `date` validation requires omitting
                // the time component
                startDate: startDateISO.substring(0, startDateISO.indexOf('T')),
                endDate: endDateISO.substring(0, endDateISO.indexOf('T')),
                projectName: customProjectName as string,
              },
            },
            getMutateOptions(DiscoveryCallModalStage.Match_Confirmed),
          );
        }
      },
    };
  } else {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  }
};

const getSecondaryCTA = (
  modalState: DiscoveryCallModalStage,
  setModalState: (stage: DiscoveryCallModalStage) => void,
  closeModal: () => void,
): ModalCallToAction | undefined => {
  if (modalState === DiscoveryCallModalStage.Update_Selection) {
    return {
      text: 'Cancel',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Unresponsive) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Other) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Declining) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Outcome_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Declined) {
    return undefined;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirmed) {
    return {
      text: 'Close',
      callback: () => {
        closeModal();
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Need_Help) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Outcome_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Outcome_Selection) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Update_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Match_Confirming) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Outcome_Selection);
      },
    };
  } else if (modalState === DiscoveryCallModalStage.Custom_Naming) {
    return {
      text: 'Go back',
      callback: () => {
        setModalState(DiscoveryCallModalStage.Match_Confirming);
      },
    };
  }
};

const getProgress = (modalState: DiscoveryCallModalStage): number => {
  if (modalState === DiscoveryCallModalStage.Update_Selection) {
    return 10;
  } else if (modalState === DiscoveryCallModalStage.Unresponsive) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Other) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Match_Declining) {
    return 70;
  } else if (modalState === DiscoveryCallModalStage.Match_Declined) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirmed) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Match_Need_Help) {
    return 60;
  } else if (modalState === DiscoveryCallModalStage.Update_Confirmed) {
    return 100;
  } else if (modalState === DiscoveryCallModalStage.Outcome_Selection) {
    return 30;
  } else if (modalState === DiscoveryCallModalStage.Match_Confirming) {
    return 50;
  } else if (modalState === DiscoveryCallModalStage.Custom_Naming) {
    return 70;
  } else {
    return 20;
  }
};
